import type { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import {
  Calendar,
  Clock,
  Database,
  FileText,
  FolderInput,
  FolderOutput,
  Hash,
  Lock,
  Mail,
  RefreshCw,
  Save,
  Settings,
  User,
} from 'lucide-react'
import type {
  EffectiveCaseSettings,
  UpdateCaseSettingsRequest,
} from '../../../types/case-container'

export function LoadingState({ t }: { t: TFunction<'cases'> }): ReactElement {
  return (
    <div className="flex h-64 items-center justify-center">
      <div className="flex flex-col items-center gap-3">
        <RefreshCw className="h-6 w-6 animate-spin text-[#4e5e82]" />
        <p className="text-sm text-slate-500 dark:text-slate-400">
          {t('caseSettings.loadingSettings')}
        </p>
      </div>
    </div>
  )
}

export function ErrorState({
  error,
  onRetry,
  t,
}: {
  error: string
  onRetry: () => void
  t: TFunction<'cases'>
}): ReactElement {
  return (
    <div className="flex h-64 items-center justify-center">
      <div className="text-center">
        <p className="text-red-600 dark:text-red-400">{error}</p>
        <button
          onClick={onRetry}
          className="mt-4 text-sm text-lime-600 hover:underline dark:text-lime-400"
        >
          {t('actions.tryAgain')}
        </button>
      </div>
    </div>
  )
}

export function SettingsHeader({
  editMode,
  onEdit,
  t,
}: {
  editMode: boolean
  onEdit: () => void
  t: TFunction<'cases'>
}): ReactElement {
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-lime-100 dark:bg-lime-950/50">
          <Settings className="h-5 w-5 text-lime-600 dark:text-lime-400" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
            {t('caseSettings.title')}
          </h2>
          <p className="text-sm text-slate-500 dark:text-slate-400">{t('caseSettings.subtitle')}</p>
        </div>
      </div>
      {!editMode && (
        <button
          onClick={onEdit}
          className="rounded-lg bg-[#4e5e82] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[#5c6e94]"
        >
          {t('caseSettings.editSettings')}
        </button>
      )}
    </div>
  )
}

interface SettingsFieldProps {
  icon: React.ReactNode
  label: string
  inherited?: boolean
  editMode: boolean
  disabled?: boolean
  value: string | null
  editValue: string
  onChange: (value: string) => void
  placeholder?: string
  type?: 'text' | 'email'
  helpText?: string
}

export function SettingsField({
  icon,
  label,
  inherited,
  editMode,
  disabled,
  value,
  editValue,
  onChange,
  placeholder,
  type = 'text',
  helpText,
}: SettingsFieldProps): ReactElement {
  const { t } = useTranslation('cases')
  return (
    <div className="rounded-lg border border-slate-200 p-4 dark:border-slate-700">
      <div className="mb-2 flex items-center gap-2">
        {icon}
        <label className="text-sm font-medium text-slate-700 dark:text-slate-300">{label}</label>
        {inherited && (
          <span className="text-xs text-[#4e5e82] dark:text-lime-400">
            {t('caseSettings.inherited')}
          </span>
        )}
      </div>
      {editMode ? (
        <>
          <input
            type={type}
            value={editValue}
            onChange={(e) => {
              onChange(e.target.value)
            }}
            disabled={disabled}
            className={`w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 ${disabled ? 'cursor-not-allowed opacity-50' : ''}`}
            placeholder={placeholder}
          />
          {helpText && (
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{helpText}</p>
          )}
        </>
      ) : (
        <p className="text-slate-900 dark:text-slate-100">{value ?? t('caseSettings.notSet')}</p>
      )}
    </div>
  )
}

interface SectionProps {
  settings: EffectiveCaseSettings
  formData: UpdateCaseSettingsRequest
  setFormData: React.Dispatch<React.SetStateAction<UpdateCaseSettingsRequest>>
  editMode: boolean
  isInherited: (fieldName: string) => boolean
  t: TFunction<'cases'>
}

function setField(
  setFormData: SectionProps['setFormData'],
  field: keyof UpdateCaseSettingsRequest,
  value: string | number | null
): void {
  setFormData((prev) => ({ ...prev, [field]: value }))
}

export function ClientSection(props: SectionProps): ReactElement {
  const { settings, formData, setFormData, editMode, isInherited, t } = props
  const fieldChange =
    (field: keyof UpdateCaseSettingsRequest) =>
    (v: string): void => {
      setField(setFormData, field, v || null)
    }
  return (
    <div>
      <h3 className="mb-4 text-sm font-semibold tracking-wider text-slate-500 uppercase dark:text-slate-400">
        {t('caseSettings.clientSection')}
      </h3>
      <div className="grid gap-6 sm:grid-cols-2">
        <SettingsField
          icon={<Hash className="h-4 w-4 text-slate-400" />}
          label={t('caseSettings.endClientReference')}
          inherited={isInherited('endClientReference')}
          editMode={editMode}
          value={settings.endClientReference}
          editValue={formData.endClientReference ?? ''}
          onChange={fieldChange('endClientReference')}
          placeholder={t('caseSettings.endClientReferencePlaceholder')}
        />
        <SettingsField
          icon={<User className="h-4 w-4 text-slate-400" />}
          label={t('caseSettings.clientContactName')}
          inherited={isInherited('endClientContactName')}
          editMode={editMode}
          value={settings.endClientContactName}
          editValue={formData.endClientContactName ?? ''}
          onChange={fieldChange('endClientContactName')}
          placeholder={t('caseSettings.clientContactNamePlaceholder')}
        />
        <SettingsField
          icon={<Mail className="h-4 w-4 text-slate-400" />}
          label={t('caseSettings.clientContactEmail')}
          inherited={isInherited('endClientContactEmail')}
          editMode={editMode}
          value={settings.endClientContactEmail}
          editValue={formData.endClientContactEmail ?? ''}
          onChange={fieldChange('endClientContactEmail')}
          placeholder={t('infrastructure.placeholders.contactEmail')}
          type="email"
        />
        <SettingsField
          icon={<FileText className="h-4 w-4 text-slate-400" />}
          label={t('caseSettings.externalCaseName')}
          inherited={isInherited('externalCaseName')}
          editMode={editMode}
          value={settings.externalCaseName}
          editValue={formData.externalCaseName ?? ''}
          onChange={fieldChange('externalCaseName')}
          placeholder={t('caseSettings.externalCaseNamePlaceholder')}
        />
      </div>
    </div>
  )
}

export function EvidenceSection(props: SectionProps): ReactElement {
  const { settings, formData, setFormData, editMode, isInherited, t } = props
  const fieldChange =
    (field: keyof UpdateCaseSettingsRequest) =>
    (v: string): void => {
      setField(setFormData, field, v || null)
    }
  const evidenceIcon = settings.isEvidenceFormatLocked ? (
    <Lock className="h-4 w-4 text-amber-500" />
  ) : (
    <Hash className="h-4 w-4 text-slate-400" />
  )
  const evidenceHelp = settings.isEvidenceFormatLocked
    ? t('caseSettings.evidenceFormatLocked')
    : t('caseSettings.evidenceNumberFormatHelp')
  return (
    <div>
      <h3 className="mb-4 text-sm font-semibold tracking-wider text-slate-500 uppercase dark:text-slate-400">
        {t('caseSettings.evidenceSection')}
      </h3>
      <div className="grid gap-6 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <SettingsField
            icon={evidenceIcon}
            label={t('caseSettings.evidenceNumberFormat')}
            inherited={isInherited('evidenceNumberFormat')}
            editMode={editMode}
            disabled={settings.isEvidenceFormatLocked}
            value={settings.evidenceNumberFormat}
            editValue={formData.evidenceNumberFormat ?? ''}
            onChange={fieldChange('evidenceNumberFormat')}
            placeholder="{case_ref}_{node_id}_{sequence:02}"
            helpText={evidenceHelp}
          />
        </div>
        <SettingsField
          icon={<FolderOutput className="h-4 w-4 text-slate-400" />}
          label={t('caseSettings.velociraptorExportPath')}
          inherited={isInherited('velociraptorExportPath')}
          editMode={editMode}
          value={settings.velociraptorExportPath}
          editValue={formData.velociraptorExportPath ?? ''}
          onChange={fieldChange('velociraptorExportPath')}
          placeholder={t('infrastructure.placeholders.exportPath')}
        />
        <SettingsField
          icon={<FolderInput className="h-4 w-4 text-slate-400" />}
          label={t('caseSettings.evidenceImportPath')}
          inherited={isInherited('evidenceImportPath')}
          editMode={editMode}
          value={settings.evidenceImportPath}
          editValue={formData.evidenceImportPath ?? ''}
          onChange={fieldChange('evidenceImportPath')}
          placeholder={t('infrastructure.placeholders.importPath')}
        />
        <SettingsField
          icon={<FolderOutput className="h-4 w-4 text-slate-400" />}
          label={t('caseSettings.exportPath')}
          inherited={isInherited('exportPath')}
          editMode={editMode}
          value={settings.exportPath}
          editValue={formData.exportPath ?? ''}
          onChange={fieldChange('exportPath')}
          placeholder={t('infrastructure.placeholders.reportPath')}
        />
      </div>
    </div>
  )
}

interface LabelledSelectFieldProps {
  icon: ReactElement
  label: string
  inherited: boolean
  editMode: boolean
  inheritedLabel: string
  children: ReactElement
}

function LabelledFieldShell({
  icon,
  label,
  inherited,
  inheritedLabel,
  children,
}: LabelledSelectFieldProps): ReactElement {
  return (
    <div className="rounded-lg border border-slate-200 p-4 dark:border-slate-700">
      <div className="mb-2 flex items-center gap-2">
        {icon}
        <label className="text-sm font-medium text-slate-700 dark:text-slate-300">{label}</label>
        {inherited && (
          <span className="text-xs text-[#4e5e82] dark:text-lime-400">{inheritedLabel}</span>
        )}
      </div>
      {children}
    </div>
  )
}

function DateFormatField({
  settings,
  formData,
  setFormData,
  editMode,
  isInherited,
  t,
}: SectionProps): ReactElement {
  const display = editMode ? (
    <select
      value={formData.dateFormat ?? ''}
      onChange={(e) => {
        setField(setFormData, 'dateFormat', e.target.value || null)
      }}
      className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
    >
      <option value="YYYY-MM-DD">{t('caseSettings.dateFormatISO')}</option>
      <option value="MM/DD/YYYY">{t('caseSettings.dateFormatUS')}</option>
      <option value="DD/MM/YYYY">{t('caseSettings.dateFormatEU')}</option>
      <option value="DD-MM-YYYY">{t('caseSettings.dateFormatAU')}</option>
    </select>
  ) : (
    <p className="text-slate-900 dark:text-slate-100">{settings.dateFormat}</p>
  )
  return (
    <LabelledFieldShell
      icon={<Calendar className="h-4 w-4 text-slate-400" />}
      label={t('caseSettings.dateFormat')}
      inherited={isInherited('dateFormat')}
      inheritedLabel={t('caseSettings.inherited')}
      editMode={editMode}
    >
      {display}
    </LabelledFieldShell>
  )
}

function TimeFormatField({
  settings,
  formData,
  setFormData,
  editMode,
  isInherited,
  t,
}: SectionProps): ReactElement {
  const display = editMode ? (
    <select
      value={formData.timeFormat ?? ''}
      onChange={(e) => {
        setField(setFormData, 'timeFormat', e.target.value || null)
      }}
      className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
    >
      <option value="24h">{t('caseSettings.time24h')}</option>
      <option value="12h">{t('caseSettings.time12h')}</option>
    </select>
  ) : (
    <p className="text-slate-900 dark:text-slate-100">{settings.timeFormat}</p>
  )
  return (
    <LabelledFieldShell
      icon={<Clock className="h-4 w-4 text-slate-400" />}
      label={t('caseSettings.timeFormat')}
      inherited={isInherited('timeFormat')}
      inheritedLabel={t('caseSettings.inherited')}
      editMode={editMode}
    >
      {display}
    </LabelledFieldShell>
  )
}

function RetentionDaysField({
  settings,
  formData,
  setFormData,
  editMode,
  isInherited,
  t,
}: SectionProps): ReactElement {
  const display = editMode ? (
    <input
      type="number"
      value={formData.retentionPeriodDays ?? ''}
      onChange={(e) => {
        setField(
          setFormData,
          'retentionPeriodDays',
          e.target.value ? parseInt(e.target.value) : null
        )
      }}
      className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
      placeholder={t('caseSettings.retentionDaysPlaceholder')}
    />
  ) : (
    <p className="text-slate-900 dark:text-slate-100">
      {t('caseSettings.retentionDaysValue', { days: settings.retentionPeriodDays })}
    </p>
  )
  return (
    <LabelledFieldShell
      icon={<Database className="h-4 w-4 text-slate-400" />}
      label={t('caseSettings.retentionDays')}
      inherited={isInherited('retentionPeriodDays')}
      inheritedLabel={t('caseSettings.inherited')}
      editMode={editMode}
    >
      {display}
    </LabelledFieldShell>
  )
}

export function DateTimeSection(props: SectionProps): ReactElement {
  const { settings, formData, setFormData, editMode, isInherited, t } = props
  const fieldChange =
    (field: keyof UpdateCaseSettingsRequest) =>
    (v: string): void => {
      setField(setFormData, field, v || null)
    }
  return (
    <div>
      <h3 className="mb-4 text-sm font-semibold tracking-wider text-slate-500 uppercase dark:text-slate-400">
        {t('caseSettings.dateTimeSection')}
      </h3>
      <div className="grid gap-6 sm:grid-cols-2">
        <SettingsField
          icon={<Clock className="h-4 w-4 text-slate-400" />}
          label={t('caseSettings.timezone')}
          inherited={isInherited('timezone')}
          editMode={editMode}
          value={settings.timezone}
          editValue={formData.timezone ?? ''}
          onChange={fieldChange('timezone')}
          placeholder={t('infrastructure.placeholders.timezonePlaceholder')}
        />
        <DateFormatField {...props} />
        <TimeFormatField {...props} />
        <RetentionDaysField {...props} />
      </div>
    </div>
  )
}

export function EditFooter({
  updating,
  onCancel,
  onSave,
  t,
}: {
  updating: boolean
  onCancel: () => void
  onSave: () => void
  t: TFunction<'cases'>
}): ReactElement {
  return (
    <div className="flex justify-end gap-3 border-t border-slate-200 pt-4 dark:border-slate-700">
      <button
        onClick={onCancel}
        className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-800"
      >
        {t('actions.cancel')}
      </button>
      <button
        onClick={onSave}
        disabled={updating}
        className="inline-flex items-center gap-2 rounded-lg bg-[#4e5e82] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[#5c6e94] disabled:opacity-50"
      >
        {updating ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
        {t('caseSettings.saveChanges')}
      </button>
    </div>
  )
}
